import "./columns.scss";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import api from "api";
import { useParams } from "react-router-dom";
import { ColumnType, RootState } from "types";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import Column from "components/board/column";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "redux/reducers";
import { toast } from "react-toastify";

interface props {
    sortOption: number;
    setSortOption: Dispatch<SetStateAction<number>>;
    viewArchived: boolean;
}

function Columns({ sortOption, setSortOption, viewArchived }: props) {
    const params: any = useParams();
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.user);

    const { board } = useSelector((state: RootState) => state);
    const [columns, setColumns] = useState<ColumnType[]>([]);
    const [dragging, setDragging] = useState(false);
    const [newColumn, setNewColumn] = useState({
        open: false,
        name: "",
    });

    const addColumn = () => {
        api.column.add(params.boardId, newColumn.name, columns.length + 1, 1).then(() => {
            const copiedData = [...columns];
            copiedData[columns.length - 1] = {
                ...copiedData[columns.length - 1],
                Tickets: copiedData[columns.length - 1].Tickets.map((x) => {
                    api.ticket.complete(x.TicketDetails.Id, user.Id, false);
                    return {
                        ...x,
                        IsCompleted: false,
                    };
                }),
            };
            setColumns(copiedData);
            getColumns();
            setNewColumn({
                open: false,
                name: "",
            });
        });
    };

    const removeAllCardsFromColumns = (columnsId: number) => {
        var oldState = [...columns];

        for (var c of oldState) {
            if (c.Column.Id === columnsId) {
                c.Tickets = [];
            }
        }
        setColumns(oldState);
    };

    const onDragEnd = (e: any) => {
        setDragging(false);
        const { source, destination } = e;

        if (!destination) return;

        if (e.type === "column") {
            const column = columns[source.index];
            const copiedData = [...columns];
            const [removed] = copiedData.splice(source.index, 1);
            copiedData.splice(destination.index, 0, removed);
            api.column.update(params.clientId, params.boardId, column.Column.Id, column.Column.Description, destination.index);
            for (var i in copiedData) {
                const isCompleted = parseInt(i) === columns.length - 1;
                copiedData[i] = {
                    ...copiedData[i],
                    Tickets: copiedData[i].Tickets.map((x) => {
                        if (params.ticketId && parseInt(params.ticketId) === x.TicketDetails.Id) {
                            dispatch(
                                actions.ticket.set({
                                    ...x,
                                    IsCompleted: isCompleted,
                                })
                            );
                        }
                        return {
                            ...x,
                            IsCompleted: isCompleted,
                        };
                    }),
                };
            }
            setColumns(copiedData);
        } else {
            api.ticket.updatePosition(parseInt(e.draggableId.slice(2)), params.clientId, columns[parseInt(destination.droppableId)].Column.Id, destination.index);
            const copiedData = [...columns];
            const fromColumn = copiedData[parseInt(source.droppableId)];
            const toColumn = copiedData[parseInt(destination.droppableId)];
            const [removed] = fromColumn.Tickets.splice(source.index, 1);
            const isCompleted = parseInt(destination.droppableId) === columns.length - 1;
            api.ticket.complete(parseInt(e.draggableId.slice(2)), user.Id, isCompleted);
            toColumn.Tickets.splice(destination.index, 0, {
                ...removed,
                TicketDetails: {
                    ...removed.TicketDetails,
                    ColumnId: toColumn.Column.Id,
                    IsCompleted: isCompleted,
                },
            });
            setColumns(copiedData);
            if (params.ticketId && parseInt(params.ticketId) === parseInt(e.draggableId)) {
                dispatch(
                    actions.ticket.set({
                        ...removed,
                        ColumnId: toColumn.Column.Id,
                        IsCompleted: isCompleted,
                    })
                );
            }
        }
    };

    const deleteColumn = (id: number) => {
        const index = columns.findIndex((x) => x.Column.Id === id);
        if (index === 0) {
            toast.warning("There must be at least 1 column");
            return;
        }

        if (index === columns.length - 1) {
            const copiedData = [...columns];
            copiedData[index - 1] = {
                ...copiedData[index - 1],
                Tickets: copiedData[index - 1].Tickets.map((x) => {
                    api.ticket.complete(x.TicketDetails.Id, user.Id, true);
                    return {
                        ...x,
                        IsCompleted: true,
                    };
                }),
            };
            setColumns(copiedData.filter((x) => x.Column.Id !== id));
            api.column.delete(id);
        } else {
            setColumns(columns.filter((x) => x.Column.Id !== id));
            api.column.delete(id);
        }
    };

    const getColumns = async () => {
        let sortPreference = localStorage.getItem("sortPreference");
        var defaultSort = 2;

        if (board?.Board?.Id) {
            if (sortPreference == null || +sortPreference === 0) {
                if (viewArchived) {
                    api.column.getAllArchived(params.boardId).then((data) => {
                        setColumns(data);
                    });
                } else {
                    api.column.getAllSorted(params.boardId, defaultSort).then((data) => {
                        setColumns(data);
                    });
                }
            } else {
                api.column.getAllSorted(params.boardId, sortOption).then((data) => {
                    setColumns(data);
                });
            }
        }
    };

    useEffect(() => {
        getColumns();
        // console.log("COLLO", columns);
    }, [board, board?.Board?.Id, params.boardId, sortOption, viewArchived]);

    return (
        <DragDropContext onDragEnd={(e) => onDragEnd(e)} onDragStart={() => setDragging(true)}>
            <Droppable droppableId="columns" direction="horizontal" type="column">
                {(provided) => {
                    return (
                        <div id="column-container" ref={provided.innerRef} {...provided.droppableProps}>
                            {columns?.map((item: ColumnType, i: number) => {
                                return (
                                    <Draggable draggableId={`c_${item?.Column?.Id}`} index={i} key={item?.Column.Id?.toString()}>
                                        {(provided) => {
                                            return (
                                                <div className="column-wrapper" ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                    <Column
                                                        columns={columns}
                                                        setColumns={setColumns}
                                                        allColumns={columns}
                                                        item={item}
                                                        deleteColumn={deleteColumn}
                                                        removeAllCardsFromColumns={removeAllCardsFromColumns}
                                                        index={i}
                                                        getColumns={getColumns}
                                                    />
                                                </div>
                                            );
                                        }}
                                    </Draggable>
                                );
                            })}
                            <div
                                className={`new-column ${newColumn.open ? "open" : ""} ${dragging ? "hide" : ""}`}
                                tabIndex={0}
                                onBlur={() => {
                                    setTimeout(() => {
                                        setNewColumn({ open: false, name: "" });
                                    }, 100);
                                }}
                            >
                                <input
                                    type="text"
                                    placeholder={`${newColumn.open ? "Title" : "New Column"}`}
                                    value={newColumn.name}
                                    onFocus={() =>
                                        setNewColumn({
                                            ...newColumn,
                                            open: true,
                                        })
                                    }
                                    onChange={(e) => setNewColumn({ ...newColumn, name: e.currentTarget.value })}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            addColumn();
                                            e.currentTarget.blur();
                                        }
                                    }}
                                />
                                <button onClick={addColumn}>Add</button>
                            </div>
                            {provided.placeholder}
                        </div>
                    );
                }}
            </Droppable>
        </DragDropContext>
    );
}

export default Columns;
